
import { SimulationConfig, Ball, EscapeRing, MorphData, Vector2D } from '@/types';
import { ShapeUtils } from '@/logic/physics/ShapeUtils';
import { resolveWallCollision } from '@/logic/physics/BoundaryCollision';
import { calculateBounce } from '@/logic/physics/BoundaryCollision';

export const handleBallCollision = (
  b: Ball,
  ringsRef: React.MutableRefObject<EscapeRing[]>,
  effectiveCfg: SimulationConfig,
  centerX: number,
  centerY: number,
  stepTS: number,
  morphData: MorphData,
  rotRad: number,
  onBreak: (ring: EscapeRing, index: number) => void,
  playCollisionSound: () => void,
  onShake: (intensity: number) => void,
  onCollision: (pos: Vector2D, radius: number, color: string) => void,
  isRhythmic: boolean = false
) => {
  const relX = b.pos.x - centerX;
  const relY = b.pos.y - centerY;

  // Sync radius if growth is NONE
  if (effectiveCfg.ballGrowthMode === 'NONE') {
      b.radius = effectiveCfg.ballRadius;
  }

  const angle = Math.atan2(relY, relX);
  const dist = Math.sqrt(relX*relX + relY*relY);
  let ringsProcessed = 0;
  const maxBreaksPerFrame = effectiveCfg.hyperSpeedBatchSize || 50; 
  
  let safetyCounter = 0;
  while (ringsRef.current.length > 0 && ringsProcessed < maxBreaksPerFrame && safetyCounter < 100) {
      safetyCounter++;
      const ring = ringsRef.current[0];
      if (!ring) break;

      const originalIndex = effectiveCfg.escapeRingCount - ringsRef.current.length;
      const isOpposite = originalIndex < (effectiveCfg.escapeOppositeGapCount || 0);
      const targetGapSize = isOpposite ? (effectiveCfg.escapeOppositeGapSize || 30) : effectiveCfg.escapeGapSize;
      let gapWidth = (targetGapSize * Math.PI) / 180;
      if (effectiveCfg.escapeGapAlignment === 'FIXED_SIZE') gapWidth = ((targetGapSize * Math.PI / 180) * effectiveCfg.containerRadius) / ring.radius;
      const halfGap = gapWidth / 2;

      const shapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, morphData.from, morphData.to, morphData.factor, ring.radius, morphData.baseRadius);
      
      // Normalize angle and calculate diff once
      let normAngle = angle;
      if (normAngle < 0) normAngle += Math.PI * 2;
      let ringStart = ring.angle % (Math.PI * 2);
      if (ringStart < 0) ringStart += Math.PI * 2;
      
      // Normalize angle difference to [-PI, PI]
      let angleDiff = (normAngle - ringStart + Math.PI) % (Math.PI * 2);
      if (angleDiff < 0) angleDiff += Math.PI * 2;
      angleDiff -= Math.PI;
      
      const diff = Math.abs(angleDiff);

      // SIDE GUARDS: Check collision even if not touching the ring yet
      if (effectiveCfg.escapeSideGuards && !isRhythmic) {
          const guardLen = (effectiveCfg.escapeRingSpacing || 40) * 1.5; 
          if (dist < shapeR && dist > shapeR - guardLen) {
              const angularMargin = (b.radius + (effectiveCfg.escapeSideGuardThickness || 4) / 2) / Math.max(1, dist);
              const threshold = halfGap - angularMargin;
              
              if (diff > threshold) {
                  const isGapEnd = angleDiff > 0;
                  const edgeAngle = ringStart + (isGapEnd ? halfGap : -halfGap);
                  
                  let nx = Math.sin(edgeAngle);
                  let ny = -Math.cos(edgeAngle);
                  if (isGapEnd) { nx = -nx; ny = -ny; }

                  const dotVel = b.vel.x * nx + b.vel.y * ny;
                  if (dotVel > 0) {
                      calculateBounce(b, nx, ny, effectiveCfg);
                      const overlapDist = (diff - threshold) * dist;
                      const maxCorrection = 10;
                      const safePush = Math.min(overlapDist + 0.1, maxCorrection);
                      b.pos.x -= nx * safePush;
                      b.pos.y -= ny * safePush;
                      
                      playCollisionSound();
                      onShake(1 * effectiveCfg.shakeIntensityMultiplier);
                      
                      const contactPoint = {
                          x: b.pos.x - nx * b.radius,
                          y: b.pos.y - ny * b.radius
                      };
                      onCollision(contactPoint, b.radius, b.color);
                  }
                  break; 
              }
          }
      }

      if (dist - b.radius > shapeR) {
          ringsRef.current.shift();
          ringsProcessed++;
          continue;
      }

      if (dist + b.radius < shapeR && (!effectiveCfg.escapeSideGuards || dist < shapeR - (effectiveCfg.escapeRingSpacing || 40) * 1.5)) {
          break;
      }

      const ballAngularWidth = b.radius / ring.radius;
      const inGap = diff < (halfGap - ballAngularWidth * 0.8);
      
      if (inGap) {
          onBreak(ring, originalIndex);
          ringsRef.current.shift(); 
          ringsProcessed++;
          
          if (ringsRef.current.length === 0) {
              break;
          }
          continue;
      } else {
          // In Rhythmic mode, RhythmicLogic handles the bounce/aiming. 
          // We let it handle the velocity updates to avoid fighting.
          if (!isRhythmic) {
              const impact = resolveWallCollision(b, centerX, centerY, ring.radius, effectiveCfg, morphData, true, onCollision);
              if (impact > 0.15) { 
                  playCollisionSound(); 
                  onShake(3 * effectiveCfg.shakeIntensityMultiplier);
              }
          } else {
              // Just provide visual/haptic feedback for the "hit" in rhythmic
              onShake(1.5);
          }
          break; 
      }
  }
};
